import {
  createStyles,
  Flex,
  Stepper,
  Text,
  Collapse,
  Button,
  useMantineTheme,
  Divider,
  Tooltip,
  SimpleGrid,
  Box,
} from "@mantine/core";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { InfoCircle, Information, TickCircle, Warning2 } from "iconsax-react";
import { useConfigContext } from "../ConfigProvider";
import {
  AcceptProductTerms,
  NumberInputControlled,
  Radio,
  RadioGroup,
  SelectControlled,
} from "../../../../../components";
import { formatCurrency } from "../../../../../utils/currency";
import { client_selected } from "../../../../../client";
import { useTermsByLanguage } from "../../../../../utils/GetTerms";
import TitleDescription from "../TitleDescription";
import { Link } from "react-router-dom";
import { api } from "../../../../../api";
import ConfirmThatYouRead from "./ConfirmThatYouRead";
import ModalOkxConnect from "../ModalOkx/ModalOkxConnect";

const useStyles = createStyles((theme) => ({
  root: {
    padding: 0,
  },

  verticalSeparator: {
    borderWidth: "4px",
    borderColor: theme.colors.basic[3],
    top: "42px",
    left: "19px",
    "&[data-completed]": {
      borderWidth: "4px",
      borderColor: theme.colors.primary[1],
    },
    "&[data-progress]": {
      borderWidth: "4px",
      borderColor: theme.colors.primary[1],
    },
  },

  verticalSeparatorActive: {
    borderWidth: "4px",
    borderColor: theme.colors.primary[1],
  },

  stepIcon: {
    borderWidth: 6,
    borderColor: theme.colors.basic[3],
    color: "transparent",
    "&[data-completed]": {
      borderWidth: 12,
      borderColor: theme.colors.primary[1],
      backgroundColor: "white",
    },
    "&[data-progress]": {
      borderWidth: 6,
      borderColor: theme.colors.primary[1],
      color: "transparent",
    },
  },

  stepCompletedIcon: {
    borderColor: "transparent",
    backgroundColor: "transparent",
  },

  stepDescription: {
    margin: 0,
    width: "100%",
  },

  step: {
    margin: 0,
    width: "100%",
    "&[disabled]": {
      cursor: "not-allowed",
    },
  },
  stepBody: {
    width: "100%",
    alignItems: "start",
  },
}));
interface IProps {
  name: string;
}

export default function FirstConfig({ name }: IProps) {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { form, product, isRefetching, states, modals } = useConfigContext();
  const brokerSelected = states.brokerSelected;
  const hasMoreThanOneOrganization = states.hasMoreThanOneOrganization;
  const urlRegistration = product?.product?.url_registration;
  const linkReferral = !!urlRegistration
    ? urlRegistration
    : process.env.REACT_APP_BITGET_REFERRAL_LINK ||
      "https://www.bitget.com/pt/expressly?channelCode=bbvf&vipCode=hwe7&languageType=12";
  const isCrypto = product?.product?.market === "CRIPTO";
  const isSpider = client_selected.name !== "Empiricus";
  const isGenial = client_selected.name === "Genial";
  const acceptTerm = form.watch("is_accepted_term");
  const hasBroker = form.watch("broker_integration_id");
  const confirmThatYouRead1 = form.watch("confirm_that_you_read_1");
  const confirmThatYouRead2 = form.watch("confirm_that_you_read_2");
  const confirmThatYouRead3 = form.watch("confirm_that_you_read_3");

  const termsLink = useTermsByLanguage();
  const qtyContract = product?.plan?.qty_contract;
  const qtyContractIsOne = qtyContract === 1;
  const hasBrokerAvailable = product?.brokers_available.length > 0;
  const isDirty = form.formState.isDirty;
  const disableNumber = states.disableNumber;
  const brokersAccountAvailable = states.brokersAccountAvailable;
  const valueSelect = states.valueSelect;
  const urlPage = window.location.href;

  const [active, setActive] = useState(hasBrokerAvailable ? 1 : 0);
  useEffect(() => {
    if (isDirty && hasBroker) {
      if (isGenial) {
        setActive(1);
      } else {
        setActive(2);
      }
    }
  }, [isDirty]);

  const handleAcceptTerm = () => {
    form.setValue("is_accepted_term", !acceptTerm);
  };

  const handleConfirmThatYouRead1 = () => {
    form.setValue("confirm_that_you_read_1", !confirmThatYouRead1);
  };
  const handleConfirmThatYouRead2 = () => {
    form.setValue("confirm_that_you_read_2", !confirmThatYouRead2);
  };
  const handleConfirmThatYouRead3 = () => {
    form.setValue("confirm_that_you_read_3", !confirmThatYouRead3);
  };

  const shouldUseOkxCredentialsModal = states.shouldUseOkxCredentialsModal;

  const handleConnectBroker = async () => {
    const nameBroker = brokerSelected?.name;

    if (!nameBroker) return;

    if (nameBroker === "Bitget") {
      const res = await api.get<{ url: string }>("connect/bitget");
      const url = res.data.url;
      return window.open(url, "_blank");
    } else if (nameBroker === "OKX") {
      // If product is in the OKX credentials list, open modal instead of OAuth
      if (shouldUseOkxCredentialsModal) {
        return modals.setOpenModalOkxCredentials(true);
      }

      const res = await api.get<{ url: string }>("okx/connect", {
        params: { redirect_url: urlPage },
      });

      const url = res.data.url;

      return window.open(url, "_blank");
    } else {
      return;
    }
  };

  const handleOkxModalSuccess = () => {
    modals.setOpenModalOkxCredentials(false);
  };

  return (
    <Flex direction={"column"} w="100%" gap={24}>
      <Flex direction={"column"} gap={6} w="100%">
        <Text
          fz="32px"
          h={{ base: "fit-content", 991: "50px" }}
          color="basic.5"
          weight={600}
        >
          {t(
            "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.title"
          )}
        </Text>
        <Text
          fz="18px"
          h={{ base: "fit-content", 1300: "28px" }}
          color="basic.4"
        >
          <Trans
            i18nKey="meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.subtitle"
            values={{ name }}
            components={{
              Name: (
                <Text fz="18px" color="basic.5" weight={500} component="span" />
              ),
            }}
          />
        </Text>
        <Divider w="100%" maw={"350px"} />
      </Flex>

      <Stepper
        active={active}
        onStepClick={setActive}
        orientation="vertical"
        classNames={classes}
        completedIcon={
          <Flex style={{ borderRadius: "50%" }}>
            <TickCircle
              size={32}
              variant="Bold"
              color={theme.colors.primary[1]}
            />
          </Flex>
        }
      >
        {isGenial ? null : (
          <Stepper.Step
            description={
              <Flex
                direction={"column"}
                gap={15}
                align={"start"}
                pb="20px"
                mb="20px"
                w="100%"
                style={{
                  borderBottom: "1px solid",
                  borderColor: theme.colors.basic[2],
                }}
              >
                <TitleDescription
                  number={1}
                  title={t(
                    "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.title",
                    { broker: brokerSelected?.name || "" }
                  )}
                  description={t(
                    "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.description"
                  )}
                />
                <Collapse
                  in={active === 0}
                  transitionDuration={300}
                  transitionTimingFunction="ease-in-out"
                >
                  <Flex direction={"column"} gap={"30px"}>
                    {hasMoreThanOneOrganization ? (
                      <SimpleGrid
                        breakpoints={[{ minWidth: 991, cols: 2 }]}
                        spacing={"30px"}
                      >
                        <SelectControlled
                          name="brokerSelected"
                          label={t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.placeholder_2"
                          )}
                          placeholder={t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.placeholder_2"
                          )}
                          w={"100%"}
                          data={states.brokersAvailable}
                          disabled={!states.isEnabledToEdit}
                        />
                      </SimpleGrid>
                    ) : null}
                    {brokerSelected ? (
                      <SimpleGrid
                        breakpoints={[{ minWidth: 991, cols: 2 }]}
                        spacing={"30px"}
                      >
                        <Flex
                          direction={"column"}
                          justify={"space-between"}
                          w="100%"
                          gap="20px"
                        >
                          <Box
                            p="md"
                            bg={theme.colors.alertsOpacity[1]}
                            style={{
                              borderLeft: `4px solid ${theme.colors.alerts[1]}`,
                              borderRadius: "6px",
                            }}
                          >
                            <Flex gap="sm" align="flex-start">
                              <Box>
                                <Information
                                  size={32}
                                  color={theme.colors.alerts[1]}
                                />
                              </Box>
                              <Box>
                                <Text fw={600} fz="14px" mb={4}>
                                  {t(
                                    "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.text_1.0",
                                    { broker: brokerSelected?.name || "" }
                                  )}
                                </Text>

                                <Text fz="14px">
                                  {t(
                                    "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.text_1.1",
                                    { broker: brokerSelected?.name || "" }
                                  )}
                                </Text>
                                <Text fz="14px">
                                  {t(
                                    "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.text_1.2"
                                  )}
                                </Text>
                                <Text fz="14px">
                                  {t(
                                    "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.text_1.3",
                                    { broker: brokerSelected?.name || "" }
                                  )}
                                </Text>
                                <Text fz="14px">
                                  {t(
                                    "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.text_1.4",
                                    { broker: brokerSelected?.name || "" }
                                  )}
                                </Text>
                              </Box>
                            </Flex>
                          </Box>
                          <Button
                            variant="primary"
                            onClick={handleConnectBroker}
                          >
                            {t(
                              "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.button_1",
                              { broker: brokerSelected?.name || "" }
                            )}
                          </Button>
                        </Flex>
                        <Flex
                          direction={"column"}
                          justify={"space-between"}
                          w="100%"
                          gap="20px"
                        >
                          <Text fz="15px" color="basic.5">
                            {t(
                              "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.text_2",
                              { broker: brokerSelected?.name || "" }
                            )}
                          </Text>
                          <Button
                            variant="primaryOutline"
                            component={Link}
                            to={linkReferral}
                            target="_blank"
                          >
                            {t(
                              "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_1.button_2",
                              { broker: brokerSelected?.name || "" }
                            )}
                          </Button>
                        </Flex>
                      </SimpleGrid>
                    ) : null}
                  </Flex>
                </Collapse>
              </Flex>
            }
          />
        )}
        <Stepper.Step
          disabled={!hasBrokerAvailable}
          description={
            <Flex
              direction={"column"}
              gap={15}
              align={"start"}
              pb="20px"
              w="100%"
              mb="20px"
              style={{
                borderBottom: "1px solid",
                borderColor: theme.colors.basic[2],
                zIndex: 1000,
              }}
            >
              <TitleDescription
                number={isGenial ? 1 : 2}
                title={t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_2.title",
                  {
                    finish_step: !hasBrokerAvailable
                      ? " (termine a etapa anterior)"
                      : "",
                    name: product?.product?.title,
                  }
                )}
                description={t(
                  isSpider
                    ? "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_2.description_1"
                    : "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_2.description_2"
                )}
              />

              <Collapse
                in={isGenial ? active === 0 : active === 1}
                transitionDuration={300}
                transitionTimingFunction="ease-in-out"
                w={"100%"}
              >
                <SimpleGrid
                  breakpoints={[
                    { minWidth: 700, cols: 2 },
                    { minWidth: 1200, cols: 3 },
                    { minWidth: 1400, cols: 4 },
                  ]}
                >
                  <SelectControlled
                    name="broker_integration_id"
                    label={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.label"
                    )}
                    placeholder={
                      isRefetching
                        ? t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.loading"
                          )
                        : t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.placeholder"
                          )
                    }
                    data={
                      isRefetching
                        ? [
                            {
                              value: "",
                              label: t(
                                "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.loading"
                              ),
                            },
                          ]
                        : brokersAccountAvailable
                    }
                    w={"100%"}
                    value={
                      isRefetching
                        ? t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.select_controlled_broker.loading"
                          )
                        : !!form.watch("broker_integration_id")
                        ? form.watch("broker_integration_id")?.toString()
                        : undefined
                    }
                    style={{
                      position: "static",
                    }}
                  />

                  <NumberInputControlled
                    name="value"
                    min={valueSelect.min_value_config}
                    max={valueSelect.max_value_config}
                    step={valueSelect.multiply_value_config}
                    w={"100%"}
                    disabled={!valueSelect.has_value_config}
                    placeholder={t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.placeholder"
                    )}
                    parser={(value) => value.replace(/\s?US\$|,*/g, "")}
                    formatter={(value) => {
                      const numericValue = parseFloat(value);
                      if (!Number.isNaN(numericValue)) {
                        const roundedValue =
                          Math.round(
                            numericValue / valueSelect.multiply_value_config
                          ) * valueSelect.multiply_value_config;

                        return `US$ ${roundedValue}`.replace(
                          /\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g,
                          ","
                        );
                      }

                      return "US$ ";
                    }}
                    label={
                      <Flex
                        gap={8}
                        align="center"
                        justify={"space-between"}
                        w="100%"
                      >
                        {t(
                          "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.label"
                        )}
                        <Tooltip
                          multiline
                          position="top"
                          label={
                            <Flex maw="340px" w={"340px"}>
                              <Text
                                style={{
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                  width: "340px",
                                }}
                              >
                                {t(
                                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.tooltip_1",
                                  {
                                    min: formatCurrency(
                                      valueSelect.min_value_config,
                                      false
                                    ),
                                  }
                                )}
                                <br />
                                {t(
                                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.tooltip_2",
                                  {
                                    max: formatCurrency(
                                      valueSelect.max_value_config,
                                      false
                                    ),
                                  }
                                )}
                                <br />
                                {t(
                                  "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_value.tooltip_3",
                                  {
                                    multiply: formatCurrency(
                                      valueSelect.multiply_value_config,
                                      false
                                    ),
                                  }
                                )}
                              </Text>
                            </Flex>
                          }
                          maw="360px"
                          withArrow
                          transitionProps={{ duration: 200 }}
                        >
                          <InfoCircle size={14} color={theme.colors.basic[5]} />
                        </Tooltip>
                      </Flex>
                    }
                  />
                  {qtyContractIsOne ? null : (
                    <NumberInputControlled
                      name="qty"
                      min={1}
                      max={disableNumber.max}
                      w={"100%"}
                      disabled={disableNumber.disable}
                      placeholder={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.number_input_qty.placeholder"
                      )}
                      label={
                        <Flex
                          gap={8}
                          align="center"
                          justify={"space-between"}
                          w="100%"
                        >
                          {disableNumber.label}
                          {disableNumber.tooltip ? (
                            <Tooltip
                              multiline
                              label={disableNumber.tooltip}
                              withArrow
                              w={"360px"}
                              position="top"
                              transitionProps={{ duration: 200 }}
                            >
                              <InfoCircle
                                size={14}
                                color={theme.colors.basic[5]}
                              />
                            </Tooltip>
                          ) : null}
                        </Flex>
                      }
                    />
                  )}
                  {!!product?.product?.has_reverse_order &&
                  isSpider &&
                  !isCrypto ? (
                    <RadioGroup
                      label={t(
                        "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.radio_group.label"
                      )}
                      value={
                        !!form.watch("is_inverse_side") ? "inversa" : "favor"
                      }
                      onChange={(value) =>
                        form.setValue("is_inverse_side", value === "inversa")
                      }
                      w={"100%"}
                    >
                      <Flex mt="8px" gap="24px" h="56px" align={"center"}>
                        <Radio
                          value="favor"
                          label={t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.radio_group.inverse"
                          )}
                          disabled={!states.isEnabledToEdit}
                        />
                        <Radio
                          value="inversa"
                          label={t(
                            "meu_portfolio.minhas_compras.configuracao_do_serviço.accordion_config.radio_group.favor"
                          )}
                          disabled={!states.isEnabledToEdit}
                        />
                      </Flex>
                    </RadioGroup>
                  ) : null}
                </SimpleGrid>
              </Collapse>
            </Flex>
          }
        />
        <Stepper.Step
          disabled={!isDirty}
          description={
            <Flex
              direction={"column"}
              gap={15}
              align={"start"}
              pb="20px"
              w="100%"
            >
              <TitleDescription
                number={isGenial ? 2 : 3}
                title={t(
                  "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_3.title",
                  {
                    finish_step: !hasBrokerAvailable
                      ? " (termine a etapa anterior)"
                      : "",
                  }
                )}
                description={t(
                  isSpider
                    ? "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_3.description_1"
                    : "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_3.description_2"
                )}
                disabled={!isDirty}
              />
              <Collapse
                in={isGenial ? active === 1 : active === 2}
                transitionDuration={300}
                transitionTimingFunction="ease-in-out"
              >
                <Flex direction={"column"} gap={8} pt={8}>
                  <AcceptProductTerms
                    acceptTerm={acceptTerm}
                    handleAcceptTerm={handleAcceptTerm}
                    link={product?.product?.term_of_contract || termsLink}
                  />
                  <ConfirmThatYouRead
                    isConfirmThatYouRead={confirmThatYouRead1}
                    handleConfirmThatYouRead={handleConfirmThatYouRead1}
                    text={
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_3.text_confirm_that_you_read_1"
                    }
                  />
                  <ConfirmThatYouRead
                    isConfirmThatYouRead={confirmThatYouRead2}
                    handleConfirmThatYouRead={handleConfirmThatYouRead2}
                    text={
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_3.text_confirm_that_you_read_2"
                    }
                  />
                  <ConfirmThatYouRead
                    isConfirmThatYouRead={confirmThatYouRead3}
                    handleConfirmThatYouRead={handleConfirmThatYouRead3}
                    text={
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_3.text_confirm_that_you_read_3"
                    }
                  />
                  <Button
                    variant={"primary"}
                    maw="295px"
                    w={"100%"}
                    onClick={() => modals.setOpenActiveAlgorithm(true)}
                    disabled={
                      !hasBroker ||
                      !acceptTerm ||
                      !confirmThatYouRead1 ||
                      !confirmThatYouRead2 ||
                      !confirmThatYouRead3
                    }
                  >
                    {t(
                      "meu_portfolio.minhas_compras.configuracao_do_serviço.first_config.step_3.button_algorithm_active"
                    )}
                  </Button>
                </Flex>
              </Collapse>
            </Flex>
          }
        />
      </Stepper>

      <ModalOkxConnect
        open={modals.openModalOkxCredentials}
        setClose={() => modals.setOpenModalOkxCredentials(false)}
        setSuccess={handleOkxModalSuccess}
      />
    </Flex>
  );
}
